import React, {useState, useEffect, useRef, useCallback} from "react";
import "./UebersichtTransaktionen.css";
import {getAbteilungenOverview, getAbteilungById, downloadAbteilungPdf, subscribeToAuthReady} from "../services/apiService";
import Button from "./ui/Button";
import Tooltip from "./ui/Tooltip";
import {IconInfo, IconDetails, IconPdfDownload, IconSpinner} from "./ui/Icons";
import FilterToggleButton from "./ui/FilterToggleButton";

const UebersichtTransaktionen = ({onSelect, initialEntries}) => {
    const [filters, setFilters] = useState({
        nummer: "",
        transaktion: "",
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [entries, setEntries] = useState(initialEntries || []);
    const [loading, setLoading] = useState(!initialEntries); // wenn initialEntries vorhanden, nicht laden
    const [error, setError] = useState(null);
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);
    const [pdfDownloading, setPdfDownloading] = useState({}); // { [id]: true/false }
    const [pdfError, setPdfError] = useState(null);

    // Status-/Meldungszustand analog zu TransaktionAnlegen
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [, setSubmitMessage] = useState('');
    const statusMessageRef = useRef(null);

    const refreshEntries = useCallback(() => {
        if (initialEntries) return; // Skip fetch wenn initialEntries geliefert
        setLoading(true);
        setError(null); // Clear previous errors
        getAbteilungenOverview()
            .then((data) => {
                setEntries(data || []);
                setLoading(false);
                setError(null);
                // Keine Erfolgsmeldung anzeigen (nur Fehler sind relevant)
            })
            .catch((err) => {
                // 401 Fehler beim ersten Laden ignorieren (passiert, wenn Token noch nicht bereit)
                if (err.status === 401) {
                    console.log('Verzögere Laden der Übersicht (noch nicht authentifiziert)');
                    // Wir warten aktiv darauf, dass das Token bereit ist
                    subscribeToAuthReady(() => {
                        console.log('Token jetzt bereit, lade Übersicht erneut...');
                        refreshEntries();
                    });
                    return;
                }
                const msg = err.message || 'Fehler beim Laden der Einträge';
                setError(msg);
                setLoading(false);
                // Keine Erfolgsmeldung; Fehler werden nur über setError angezeigt
            });
    }, [initialEntries]);

    useEffect(() => {
        refreshEntries();
    }, [refreshEntries]);

    // Automatisches Verbergen der Meldung nach 5 Sekunden
    useEffect(() => {
        if (submitStatus) {
            const timer = setTimeout(() => {
                setSubmitStatus(null);
                setSubmitMessage('');
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [submitStatus]);

    // Automatisches Scrollen zur Meldung
    useEffect(() => {
        if (submitStatus && statusMessageRef.current) {
            statusMessageRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [submitStatus]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({...prev, [field]: value}));
        setCurrentPage(1);
    };

    const handleReset = () => {
        setFilters({
            nummer: "",
            transaktion: "",
        });
        setCurrentPage(1);
    };

    // Filtere Daten basierend auf Eingaben
    const filteredEntries = entries.filter((entry) => {
        const nummerMatch =
            filters.nummer.length < 1 ||
            (entry.nummer !== null && entry.nummer !== undefined && String(entry.nummer).includes(filters.nummer.trim()));

        const transaktionMatch =
            filters.transaktion.length < 2 ||
            (entry.transaktionsbezeichner && entry.transaktionsbezeichner.toLowerCase().includes(filters.transaktion.toLowerCase()));

        return nummerMatch && transaktionMatch;
    });

    // Pagination
    const totalPages = Math.ceil(filteredEntries.length / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const paginatedEntries = filteredEntries.slice(startIndex, startIndex + entriesPerPage);

    // Hilfsfunktion: Zählwerte für F/S/L im Format x/y/z ermitteln
    const getEntryCounts = (entry) => {
        // Persönliche Forderungen (F)
        const f =
            entry.anzahlPersoenlicheForderungen ??
            entry.persoenlicheForderungenCount ??
            (entry.persoenlicheForderungen ? entry.persoenlicheForderungen.length : undefined) ??
            0;
        // Sicherheiten = Grundpfandrechte (S)
        const s =
            entry.anzahlSicherheiten ??
            entry.sicherheitenCount ??
            entry.grundpfandrechteCount ??
            (entry.sicherheiten ? entry.sicherheiten.length : undefined) ??
            (entry.grundpfandrechte ? entry.grundpfandrechte.length : undefined) ??
            0;
        // Löschungen (L)
        const l =
            entry.anzahlLoeschungen ??
            entry.loeschungenCount ??
            (entry.loeschungen ? entry.loeschungen.length : undefined) ??
            0;
        return {f, s, l};
    };

    const getPageNumbers = () => {
        const pages = [];
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, start + 3);
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="page-container">
            <h2 className="page-title">Übersicht Transaktionen</h2>

            {!loading && error && (
                <div className="submit-status error" role="alert">{error}</div>
            )}

            {pdfError && (
                <div className="submit-status error" role="alert">{pdfError}</div>
            )}

            {/* Nur Fehlermeldungen sind relevant; keine Erfolgsmeldungen anzeigen */}

            {/* Filter Row */}
            <div className="top-block">
                <div className="filter-header">
                    <FilterToggleButton
                        isExpanded={isFilterExpanded}
                        onToggle={() => setIsFilterExpanded(!isFilterExpanded)}
                    />
                </div>

                {isFilterExpanded && (
                    <div className="filter-content">
                        <div className="top-block-filter-row">
                            <div className="form-field">
                                <label htmlFor="filter-abteilung">Abteilung</label>
                                <input
                                    id="filter-abteilung"
                                    type="text"
                                    value={filters.nummer}
                                    onChange={(e) =>
                                        handleFilterChange("nummer", e.target.value)
                                    }
                                    placeholder="Nummer eingeben"
                                />
                            </div>
                            <div className="form-field">
                                <label htmlFor="filter-transaktion">Transaktion</label>
                                <input
                                    id="filter-transaktion"
                                    type="text"
                                    value={filters.transaktion}
                                    onChange={(e) =>
                                        handleFilterChange("transaktion", e.target.value)
                                    }
                                    placeholder="Min. 2 Zeichen eingeben"
                                />
                            </div>
                            <div className="top-block-reset-btn">
                                <div className="form-field">
                                    <label>&nbsp;</label>
                                    <Button
                                        variant="outline-primary"
                                        icon
                                        ariaLabel="Filter zurücksetzen"
                                        title="Filter zurücksetzen"
                                        tooltip="Filter zurücksetzen"
                                        onClick={handleReset}
                                    >↺</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="middle-block">
                <div className="common-section">
                    <table className="common-table">
                        <thead>
                        <tr>
                            <th>Abteilung</th>
                            <th>Transaktion</th>
                            <th>Übertragungsberechtigter</th>
                            <th>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span>Einträge</span>
                                    <Tooltip position="bottom" align="right" text={"Anzahl der Einträge\nForderungen / Sicherheiten / Löschungen"}>
                                        <IconInfo style={{ cursor: 'help' }} />
                                    </Tooltip>
                                </div>
                            </th>
                            <th>Aktionen</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="no-data">Lade Einträge...</td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan="5" className="no-data error">Einträge konnten nicht geladen werden.</td>
                            </tr>
                        ) : paginatedEntries.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="no-data">Keine Einträge gefunden</td>
                            </tr>
                        ) : (paginatedEntries.map((entry, idx) => (
                            <tr key={idx} className={`common-row${idx % 2 === 0 ? ' zebra' : ''}`}>
                                <td>{entry.nummer}</td>
                                <td>{entry.transaktionsbezeichner}</td>
                                <td>
                                    {Array.isArray(entry.uebertragungsberechtigte) && entry.uebertragungsberechtigte.length > 0 ? (
                                        <ul className="uebertragungsberechtigte-list">
                                            {entry.uebertragungsberechtigte.map((name, i) => (
                                                <li key={i}>{name}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        typeof entry.uebertragungsberechtigte === 'string' && entry.uebertragungsberechtigte.trim() !== "" ? (
                                            entry.uebertragungsberechtigte
                                        ) : "-"
                                    )}
                                </td>
                                {(() => {
                                    const {f, s, l} = getEntryCounts(entry);
                                    return (
                                        <td title="Forderungen/Sicherheiten/Löschungen">{`${f}/${s}/${l}`}</td>
                                    );
                                })()}
                                <td>
                                    <div style={{display: 'flex', gap: '4px'}}>
                                    <Button
                                        variant="primary"
                                        icon
                                        size="sm"
                                        ariaLabel="Details anzeigen"
                                        title="Details anzeigen"
                                        tooltip="Details anzeigen"
                                        tooltipAlign="right"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            getAbteilungById(entry.id)
                                                .then(details => onSelect(details))
                                                .catch(err => console.error('Fehler beim Laden der Details:', err));
                                        }}>
                                        <IconDetails />
                                    </Button>
                                    <Button
                                        variant="primary"
                                        icon
                                        size="sm"
                                        ariaLabel="Registerauszug als PDF herunterladen"
                                        title="PDF herunterladen"
                                        tooltip="Registerauszug als PDF herunterladen"
                                        tooltipAlign="right"
                                        disabled={!!pdfDownloading[entry.id]}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPdfError(null);
                                            setPdfDownloading(prev => ({...prev, [entry.id]: true}));
                                            downloadAbteilungPdf(entry.id, entry.nummer || `abteilung-${entry.id}`)
                                                .catch(err => {
                                                    setPdfError(err.message || 'Fehler beim Herunterladen des PDFs');
                                                })
                                                .finally(() => {
                                                    setPdfDownloading(prev => ({...prev, [entry.id]: false}));
                                                });
                                        }}>
                                        {pdfDownloading[entry.id] ? <IconSpinner /> : <IconPdfDownload />}
                                    </Button>
                                    </div>
                                </td>
                            </tr>
                        )))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="bottom-block">
                <div className="bottom-block-right">
                    <Button
                        variant="outline-primary"
                        icon
                        disabled={currentPage === 1}
                        ariaLabel="Vorherige Seite"
                        title="Vorherige Seite"
                        tooltip="Vorherige Seite"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                        ‹
                    </Button>
                    {getPageNumbers().map(pageNum => (
                        <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? 'primary' : 'outline-primary'}
                            size="sm"
                            ariaLabel={`Seite ${pageNum} öffnen`}
                            title={`Seite ${pageNum}`}
                            tooltip={currentPage === pageNum ? `Aktive Seite ${pageNum}` : `Seite ${pageNum} öffnen`}
                            onClick={() => setCurrentPage(pageNum)}
                        >
                            {pageNum}
                        </Button>
                    ))}
                    <Button
                        variant="outline-primary"
                        icon
                        disabled={currentPage === totalPages || totalPages === 0}
                        ariaLabel="Nächste Seite"
                        title="Nächste Seite"
                        tooltip="Nächste Seite"
                        tooltipAlign="right"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    >
                        ›
                    </Button>
                </div>

                <div className="bottom-block-right entries-per-page">
                    <select
                        value={entriesPerPage}
                        onChange={(e) => {
                            setEntriesPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10 Einträge</option>
                        <option value={20}>20 Einträge</option>
                        <option value={50}>50 Einträge</option>
                        <option value={100}>100 Einträge</option>
                    </select>
                </div>
            </div>
        </div>
    );
};

export default UebersichtTransaktionen;
